<script setup>
import { ref, computed } from 'vue'
import { noImage } from '@/common/helper'
import { useCatalogState } from '../composables/CatalogState.js'
import { useCabinetState } from '../composables/CabinetState.js'
import { useArticleStock } from '../composables/ArticleStock.js'
import PushButton from '@/components/PushButton.vue'

const {
  isEditStockQuantity,
  isStockMismatch,
  virtualStockQuantity,
  virtualStockQuantityState,
  pendingTransferQuantity,
  isCommentEdit,
  stockComment,
  canEditComment,
  hasEditCommentConditions,
  showRelatedStock,
} = useArticleStock();

const {
  selected,
  selectedTrayItem,
  isCabinetBorrowMode,
  isLargeImageView,
  isRelatedArticlesView,
  nearestParentCategoryLargeImageUrl,
  hasParameterSelected,
  parametersSelected,
  getUserDisplayName,
  canOpenTrayPermissions,
  isTakenByOtherUser,
  editComment,
  saveComment,
  cancelComment,
  applyStockTransfer,
  undoStockTransfer,
} = useCatalogState();

const {
  isReferencing,
  isOpening,
  isOpen,
  isPrevOpen,
  isClosing,
} = useCabinetState();
</script>

<template>
  <div class="flex flex-row w-full gap-8 mb-4">

    <!-- Large image -->
    <div class="grid relative w-full place-items-center" v-if="!isRelatedArticlesView && isLargeImageView" @click="() => isLargeImageView = false">
      <p class="article-title py-2 mb-4 w-full text-center">{{ selected.name }}</p>
      <img :src="selected.largeImageUrl" alt="" style="max-width: 100%" class="dark:bg-white dark:p-3 dark:rounded-md" />
      <!-- <div class="absolute bottom-1 right-2 text-gray-300" v-if="selected.mediumImageUrl">
        <i class="fa fa-compress"></i>
      </div> -->
    </div>

    <!-- Article image -->
    <div class="grid" v-if="!isRelatedArticlesView && !isLargeImageView">
      <div class="relative grid place-items-center text-gray-200 border dark:border-gray-800 rounded-md" style="width: 220px; height: 220px;" v-if="!isRelatedArticlesView">
        <img :src="selected.mediumImageUrl" alt="" class="dark:bg-white p-3 rounded-md" style="width: 220px; max-height: 220px;" @error="() => noImage(selected)" v-if="selected.mediumImageUrl" @click="() => isLargeImageView = true" />
        <img :src="nearestParentCategoryLargeImageUrl" alt="" class="dark:bg-white p-3 rounded-md" style="width: 220px; max-height: 220px;" @error="() => nearestParentCategoryLargeImageUrl = null" v-else-if="allowImageFallback && nearestParentCategoryLargeImageUrl" />
        <div class="dark:text-gray-800" style="font-size: 48px;" v-else>
          <i class="fa fa-image"></i>
        </div>
        <!-- <div class="absolute bottom-1 right-2 text-gray-300" v-if="selected.mediumImageUrl" @click="() => isLargeImageView = true">
          <i class="fa fa-expand"></i>
        </div> -->
      </div>

      <!-- Attachments, e.g. usage manual pdf -->
      <!-- <div class="grid gap-2 mt-4 w-full" v-if="false">
        <Button @click="openAttachment" type="secondary" class="text-left">
          <i class="fa fa-paperclip mr-2"></i> Specifikacija
        </Button>
        <Button @click="openAttachment" type="secondary" class="text-left">
          <i class="fa fa-paperclip mr-2"></i> Navodila
        </Button>
      </div> -->

    </div>

    <!-- Article title, description, specifications -->
    <div class="flex flex-col grow gap-4 relative" v-if="!isRelatedArticlesView && !isLargeImageView">
      <!-- Title -->
      <p class="article-title font-bold">{{ selected.name }}</p>

      <!-- Description -->
      <p class="article-description" v-if="selected.description">{{ selected.description }}</p>

      <!-- Specification -->
      <p class="article-specification" v-if="hasParameterSelected && parametersSelected.length">
        <h4 class="font-semibold">Specifikacija</h4>
        <div v-for="spec in parametersSelected">{{ spec.name }}: {{ spec.value }}</div>
      </p>

      <!-- Stock quantity -->
      <p class="article-stock" v-if="!isCabinetBorrowMode">
        <div v-if="stockSum(selected.trays) > 0">
          <span v-if="selected?.trays?.length > 1">Skupna zaloga</span><span v-else>Na zalogi</span><span class="font-medium ml-1">{{ stockSum(selected.trays) }}</span> {{ selected.stockUnit || "kos" }}
        </div>
        <div v-else-if="selected?.relatedStockAvailable && !isEditStockQuantity && !isRelatedArticlesView && !isReferencing && !isOpening && !isOpen" class="w-full">
          <div class="border border-amber-100 bg-amber-100 text-amber-800 text-sm rounded-lg px-4 py-3 dark:bg-yellow-300 dark:text-amber-900 dark:border-amber-800">
            <i class="fa fa-location-dot mr-2 text-amber-500 dark:text-amber-900"></i>
            <span>Artikla ni v tej omari - na voljo v drugi omari.</span>
          </div>
          <div class="flex justify-end w-full mt-2">
            <Button type="warning" @click="showRelatedStock(selected)">Preveri <i class="fa fa-arrow-right ml-2"></i></Button>
          </div>
        </div>
      </p>

      <!-- Status of borrowed article -->
      <div class="article-stock-status" v-if="isCabinetBorrowMode && selectedTrayItem && selectedTrayItem.usage && selectedTrayItem.countMode != 'storage'">
        <div v-if="selected?.relatedStockAvailable && !isEditStockQuantity && !isRelatedArticlesView && !isReferencing && !isOpening && !isOpen" class="w-full">
          <div class="border border-amber-100 bg-amber-100 text-amber-800 text-sm rounded-lg px-4 py-3 dark:bg-yellow-300 dark:text-amber-900 dark:border-amber-800">
            <i class="fa fa-location-dot mr-2 text-amber-500 dark:text-amber-900"></i>
            <span>Artikla je na voljo še v drugi omari.</span>
          </div>
          <div class="flex justify-end w-full mt-2">
            <Button type="warning" @click="showRelatedStock(selected)">Preveri <i class="fa fa-arrow-right ml-2"></i></Button>
          </div>
        </div>

        <div class="label mb-1">Stanje</div>
        <Tag color="green" v-if="selectedTrayItem.usage == 'present'">V predalu</Tag>
        <Tag color="yellow" v-else-if="selectedTrayItem.usage == 'taken'">Na izposoji</Tag>
        <Tag color="red" v-else>{{ selectedTrayItem.usage }}</Tag>
      </div>

      <!-- Status of borrowed article -->
      <div class="article-stock-user" v-if="isCabinetBorrowMode && selectedTrayItem && selectedTrayItem.userId && selectedTrayItem.usage == 'taken'">
        <div class="label mb-1">Vzel</div>
        <Tag color="yellow"><i class="fa fa-user mr-1"></i> {{ getUserDisplayName(selectedTrayItem.userId) }}</Tag>
        <Tag color="yellow" class="ml-2" v-if="selectedTrayItem.userTime && selectedTrayItem.userTime instanceof Date"><i class="fa fa-clock mr-1"></i> {{ formatLocaleDate(selectedTrayItem.userTime) }} ob {{ formatLocaleTime(selectedTrayItem.userTime) }}</Tag>
      </div>

      <!-- Edit quantity -->
      <div v-if="isEditStockQuantity && !isCabinetBorrowMode">
        <div>Urejanje zaloge</div>
        <!-- <div class="flex flex-row justify-stretch mt-2">
          <PushButton type="secondary" class="whitespace-nowrap" @click="updateQuantity(-10)">-10</PushButton>
          <PushButton class="ml-2 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" style="border-radius: 7px 0 0 7px;" @click="updateQuantity(-1)"><i class="fa fa-arrow-down"></i></PushButton>
          <TextBox v-model="virtualStockQuantity" placeholder="Brez" class="w-full bg-white !rounded-sm border border-gray-300 text-gray-900 text-sm block p-2.5 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white dark:border-slate-500" />
          <PushButton class="mr-2 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" style="border-radius: 0 7px 7px 0;" @click="updateQuantity(+1)"><i class="fa fa-arrow-up"></i></PushButton>
          <PushButton type="secondary" class="whitespace-nowrap" @click="updateQuantity(+10)">+10</PushButton>
        </div> -->
        <div class="flex flex-row justify-stretch mt-2">
          <PushButton class="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" style="border-radius: 7px 0 0 7px;" @click="updateQuantity(-1)"><i class="fa fa-arrow-down"></i></PushButton>
          <TextBox v-model="virtualStockQuantity" placeholder="--" class="w-full text-center font-medium !py-1.5 !text-lg tracking-widest !rounded-sm border border-gray-300 text-gray-900 text-sm block p-2.5 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white dark:border-slate-500"
            :class="{
              '!bg-red-200 !text-red-800 dark:!bg-red-900 dark:!text-red-300': virtualStockQuantityState == 'empty',
              '!bg-amber-100 !text-amber-800 dark:!bg-yellow-900 dark:!text-yellow-300': virtualStockQuantityState == 'low',
              '!bg-amber-200 !text-amber-800 dark:!bg-yellow-900 dark:!text-yellow-300': virtualStockQuantityState == 'warning',
              '!bg-sky-100 dark:!bg-sky-900': virtualStockQuantityState == 'normal',
            }" />
          <PushButton class="mr-2 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" style="border-radius: 0 7px 7px 0;" @click="updateQuantity(+1)"><i class="fa fa-arrow-up"></i></PushButton>
          <Button type="secondary" class="whitespace-nowrap w-40" @click="rotateQuantityFactor()">&times; {{ quantityFactor }}</Button>
          <Button type="primary" class="whitespace-nowrap ml-2" @click="applyStockTransfer()" v-if="pendingTransferQuantity > 0 && !isCabinetBorrowMode && !isStockTransferApplied"><i class="fa fa-arrow-right-to-bracket rotate-180 mr-2"></i>{{ pendingTransferQuantity }}</Button>
          <Button type="warning" class="whitespace-nowrap ml-2" @click="undoStockTransfer()" v-if="pendingTransferQuantity > 0 && !isCabinetBorrowMode && isStockTransferApplied"><i class="fa fa-rotate-left mr-2"></i>{{ pendingTransferQuantity }}</Button>
        </div>

        <!-- Stock mismatch: weighted stock does not match previously stored quantity -->
        <div class="mt-2" v-if="isStockMismatch">
          <i class="fa fa-triangle-exclamation text-amber-400 ml-14 mr-2"></i><span class="text-amber-700 dark:text-amber-400">Zaloga se ne ujema!</span>
        </div>
      </div>

      <!-- Comment -->
      <div class="article-stock-status" v-if="!isCommentEdit && hasEditCommentConditions && selectedTrayItem && stockComment && stockComment.length > 0">
        <div class="label mb-1">Komentar</div>
        <pre class="text-sm font-sans text-slate-700 dark:text-white">{{ stockComment }}</pre>
      </div>

      <!-- Edit comment -->
      <div v-if="hasEditCommentConditions && isCommentEdit">
        <div>{{ $t('catalog.comment_label') }}</div>
        <div class="w-full mt-2">
          <TextArea v-model="commentText" :placeholder="$t('catalog.comment_placeholder')" class="w-full" :rows="5" />
        </div>
        <div class="flex flex-row justify-end gap-2 mt-2">
          <Button :type="'secondary'" @click="cancelComment()"><i class="fa fa-xmark mr-2"></i>{{ $t('generic.cancel') }}</Button>
          <Button :type="'success'" @click="saveComment({ submit: isCabinetBorrowMode })"><i class="fa fa-check mr-2"></i>{{ $t('generic.confirm') }}</Button>
        </div>
      </div>

      <!-- Edit comment button -->
      <Button type="secondary" @click="editComment()" v-if="hasEditCommentConditions && canEditComment && !isCommentEdit && !isReferencing && isOpen && !isPrevOpen && isEditStockQuantity"><i class="fa fa-pen-to-square mr-2"></i>Uredi komentar</Button>

      <!-- Access permissions -->
      <div class="article-access-permission border border-amber-100 bg-amber-100 text-amber-800 text-sm rounded-lg px-4 py-3 dark:bg-yellow-300 dark:text-amber-900 dark:border-amber-800" v-if="!canOpenTrayPermissions && selectedTrayItem">
        <i class="fa fa-lock mr-2 text-amber-500 dark:text-amber-900"></i>
        <span v-if="isTakenByOtherUser">Na izposoji od drugega uporabnika</span>
        <span v-else>Uporabnik nima pravic dostopa do predala</span>
      </div>

      <!-- Borrow mode -->
      <!-- <div v-if="isEditStockQuantity && isCabinetBorrowMode && isOpen">
        <div>Izberi dejanje</div>
        <div class="grid grid-cols-4 gap-2 mt-2">
          <Button :type="selectedTrayUsage == 'taken' ? 'warning' : 'secondary'" :enabled="selectedTrayUsage == 'taken'" @click="setTrayUsage('taken')"><i class="fa fa-arrow-right-from-bracket mr-2"></i>Vzel</Button>
          <Button :type="selectedTrayUsage == 'present' ? 'success' : 'secondary'" :enabled="selectedTrayUsage == 'present'" @click="setTrayUsage('present')"><i class="fa fa-arrow-right-to-bracket mr-2"></i>Vrnil</Button>
        </div>
      </div> -->
      
    </div>
  </div>
</template>